import React from "react";
import "./../css/Search.css";

interface SearchProps {
  isOpen: boolean;
  onClose: () => void;
  data: any[];
  onEventClick: (event: any) => void;
}

const Search: React.FC<SearchProps> = ({ isOpen, onClose, data, onEventClick }) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchResults, setSearchResults] = React.useState<any[]>([]);
  const [hasSearched, setHasSearched] = React.useState(false);

  if (!isOpen) return null;

  const formatDateTime = (dateTimeStr: string, isGanzTaegig: boolean) => {
    if (isGanzTaegig) return "Ganztägig";
    const date = new Date(dateTimeStr);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const formatDate = (dateTimeStr: string) => {
    const date = new Date(dateTimeStr);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch();
  };

  const performSearch = () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    const results = data.filter((event) => {
      // Ignoriere Feiertage in der Suche
      if (event.isFeiertag) return false;

      // Suche im Titel
      if (event.title?.toLowerCase().includes(term)) return true;

      // Suche im Datum
      const dateStr = formatDate(event.startTime).toLowerCase();
      if (dateStr.includes(term)) return true;

      // Suche in der Startzeit
      const startTime = formatDateTime(event.startTime, false).toLowerCase();
      if (startTime.includes(term)) return true;

      // Suche in der Endzeit
      const endTime = formatDateTime(event.endTime, false).toLowerCase();
      if (endTime.includes(term)) return true;

      return false;
    });

    setSearchResults(results);
    setHasSearched(true);
  };

  const handleEventClick = (event: any) => {
    onEventClick(event);
    onClose();
    setSearchTerm("");
    setSearchResults([]);
    setHasSearched(false);
  };

  const handleClose = () => {
    onClose();
    setSearchTerm("");
    setSearchResults([]);
    setHasSearched(false);
  };

  return (
    <div className="search-overlay" onClick={handleClose}>
      <div className="search-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="search-header">
          <h2>Termin suchen</h2>
          <button className="close-button" onClick={handleClose}>
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="search-form">
          <div className="search-input-container">
            <input
              type="text"
              className="search-input"
              placeholder="Nach Titel, Datum oder Uhrzeit suchen..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (e.target.value.trim()) {
                  performSearch();
                } else {
                  setSearchResults([]);
                  setHasSearched(false);
                }
              }}
              autoFocus
            />
            <button type="submit" className="search-button">
              🔍 Suchen
            </button>
          </div>
        </form>

        <div className="search-results">
          {!hasSearched && (
            <p className="no-results">
              Geben Sie einen Suchbegriff ein, um Termine zu finden.
            </p>
          )}
          
          {hasSearched && searchResults.length === 0 && (
            <p className="no-results">
              Keine Termine gefunden für "{searchTerm}".
            </p>
          )}

          {searchResults.length > 0 && (
            <>
              <p className="results-count">
                {searchResults.length} {searchResults.length === 1 ? "Termin" : "Termine"} gefunden
              </p>
              <div className="result-list">
                {searchResults.map((event) => {
                  const isGanzTaegig = event.ganzTaegig === 1;
                  const startTime = formatDateTime(event.startTime, false);
                  const endTime = formatDateTime(event.endTime, false);
                  const date = formatDate(event.startTime);

                  return (
                    <div
                      key={event.id}
                      className="result-item"
                      onClick={() => handleEventClick(event)}
                    >
                      <div
                        className="result-color-dot"
                        style={{ backgroundColor: event.bgColor || "#3788d8" }}
                      />
                      <div className="result-content">
                        <div className="result-title">{event.title}</div>
                        <div className="result-details">
                          <span className="result-date">📅 {date}</span>
                          <span className="result-time">
                            {isGanzTaegig ? "Ganztägig" : `⏰ ${startTime} - ${endTime}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Search;